import React, { useEffect, useRef, useState } from 'react';
import { Progress } from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, WarningOutlined,
  ExpandOutlined,
} from '@ant-design/icons';
import { getJobStatus } from '../../api/api';
import { JobStatus, JobType, JOB_TYPE_LABEL } from '../../types/job';
import { FONT_SIZE, FONT_WEIGHT, SPACING, LAYOUT, SHADOW } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useIsCompact } from '../../hooks/UseIsCompact';
import { AppButton } from '../common/AppButton';

// ─── 타입 ─────────────────────────────────────────────────────────────────────

interface Props {
  jobId:        string | null;
  onComplete:   (job: JobStatus) => void;
  onOpenDetail: () => void;
  /**
   * 이 진행바가 맡는 잡 종류를 호출자가 알면 넘긴다(예: 상품 등록 진행바 = 'REGISTER_PRODUCTS'). 서버가 첫 조회부터 잡을
   * 모를 때(404) 안내 · onComplete 의 종류로 쓴다. 여러 종류가 오가는 진행바(카드 · 재고)는 넘기지 않는다 — 그때는 '작업'.
   */
  jobType?:     JobType;
}

/**
 * 서버가 잡을 찾지 못할 때(404) 호출자에게 넘길 "실패로 끝남" 상태. 마지막으로 받은 상태가 있으면 종류 · 건수를 이어 쓴다.
 * 받은 상태가 없으면 호출자가 준 종류, 그것도 없으면 'UNKNOWN'(라벨 '작업') — 예전엔 'REGISTER_PRODUCTS' 로 채워 가격 · 재고
 * 작업도 "상품 등록 실패" 로 안내했다(감사 card-controltower#87). 호출자는 FAILED 에서 종류별 완료 처리를 하지 않고 잠금만 푼다.
 */
function goneJob(jobId: string, last: JobStatus | null, fallbackType: JobType | undefined): JobStatus {
  return {
    jobId,
    type: last?.type ?? fallbackType ?? 'UNKNOWN',
    status: 'FAILED',
    phase: last?.phase ?? '',
    processedCount: last?.processedCount ?? 0,
    total: last?.total ?? 0,
    results: null,
    successCount: last?.successCount ?? 0,
    failCount: last?.failCount ?? 0,
    dryRunCount: last?.dryRunCount ?? 0,
    deletedCount: last?.deletedCount ?? 0,
    error: '서버에서 작업 정보를 찾을 수 없습니다 — 결과는 반영 로그에서 확인해 주세요',
  };
}

// ─── JobProgressBar ───────────────────────────────────────────────────────────

/**
 * 논블로킹 잡 진행 표시줄.
 * 대시보드 레이아웃에 맞춰 사이드바(220px) 오른쪽부터 시작한다. 좁은 폭(<= 999px)에서는
 * 사이드바가 Drawer 로 접히므로(DashboardHeader·DashboardLayout·AppSidebar 와 같은
 * `useIsCompact`) 화면 왼쪽 끝에서 시작한다 — 예전엔 좁은 폭에서도 220px 를 비워 두어
 * 진행바 왼쪽이 빈 채로 남았다(2026-10-20 리뷰).
 */
export const JobProgressBar: React.FC<Props> = ({ jobId, onComplete, onOpenDetail, jobType }) => {
  const t = useThemeTokens();
  const { success, danger, warning, accent: primary } = t;
  // 헤더·본문·저장 바와 같은 compact 훅을 써서 폭 판단이 어긋나지 않게 한다.
  const compact = useIsCompact();

  const [job, setJob]         = useState<JobStatus | null>(null);
  const [visible, setVisible] = useState(false);
  const onCompleteRef         = useRef(onComplete);
  const jobTypeRef            = useRef(jobType);
  const calledRef             = useRef(false);
  const hideTimerRef          = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { onCompleteRef.current = onComplete; jobTypeRef.current = jobType; });

  // body 클래스로 DashboardLayout 에 여백 신호 전달
  useEffect(() => {
    if (visible) {
      document.body.classList.add('job-running');
    } else {
      document.body.classList.remove('job-running');
    }
    return () => { document.body.classList.remove('job-running'); };
  }, [visible]);

  // 폴링
  useEffect(() => {
    if (!jobId) return;

    setJob(null);
    setVisible(true);
    calledRef.current = false;
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }

    let cancelled = false;
    let last: JobStatus | null = null;

    // 끝난 잡은 성공 · 실패 모두 onComplete 로 알린다 — 실패를 알리지 않으면 호출자가 activeJobId 를 비우지 못해
    // 다음 작업 버튼이 "진행 중" 으로 영구히 잠긴다(감사 card-controltower#84).
    const finish = (status: JobStatus) => {
      setJob(status);
      if (!calledRef.current) {
        calledRef.current = true;
        onCompleteRef.current(status);
      }
      hideTimerRef.current = setTimeout(() => setVisible(false), status.status === 'DONE' ? 3000 : 8000);
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        const status = await getJobStatus(jobId);
        if (cancelled) return;
        last = status;

        if (status.status === 'DONE' || status.status === 'FAILED') {
          finish(status);
          return;
        }
        setJob(status);
        setTimeout(poll, 2000);
      } catch (e) {
        if (cancelled) return;
        // 안전망 — 서버가 이 잡을 모르면(404: 재시작 등으로 잡 정보가 사라짐) 더 기다려도 끝나지 않는다.
        // 실패로 끝난 것으로 보고 호출자에게 알려 잠금을 푼다.
        if ((e as { response?: { status?: number } })?.response?.status === 404) {
          finish(goneJob(jobId, last, jobTypeRef.current));
          return;
        }
        setTimeout(poll, 3000);
      }
    };
    poll();

    return () => { cancelled = true; };
  }, [jobId]);

  useEffect(() => () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);

  if (!visible) return null;

  const isRunning = !job || job.status === 'RUNNING';
  const isDone    = job?.status === 'DONE';
  const isFailed  = job?.status === 'FAILED';
  // naver.commerce.register-enabled=false라 실제 Naver 호출 없이 success=true로만
  // 끝난 경우 — "성공"이 아니라 dry-run이라는 걸 눈에 띄게 구분한다(2026-08-19,
  // NAVER_REGISTER_ENABLED 환경변수 누락으로 진행률 모달이 "성공"으로만 보이던 사고 이후 추가).
  const isDryRun  = isDone && !!job && job.dryRunCount > 0;

  const percent   = job && job.total > 0
    ? Math.round((job.processedCount / job.total) * 100)
    : 0;

  const typeLabel   = job ? (JOB_TYPE_LABEL[job.type] ?? job.type) : '작업';
  const statusColor = isDryRun ? warning : isDone ? success : isFailed ? danger : primary;
  const statusIcon  = isDryRun
    ? <WarningOutlined style={{ color: warning }} />
    : isDone
    ? <CheckCircleOutlined style={{ color: success }} />
    : isFailed
    ? <CloseCircleOutlined style={{ color: danger }} />
    : null;

  const statusText = isRunning
    ? `${typeLabel} 진행 중 — ${job ? `${job.phase} (${job.processedCount} / ${job.total})` : '준비 중...'}`
    : isDryRun
    ? `⚠️ DRY-RUN — 실제로는 등록 안 됨 (${job!.dryRunCount}건)`
    : isDone
    ? `${typeLabel} 완료 — 성공 ${job!.successCount}건${job!.failCount > 0 ? ` / 실패 ${job!.failCount}건` : ''}`
    : `${typeLabel} 실패 — ${job?.error ?? '알 수 없는 오류'}`;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      // 사이드바 오른쪽부터 시작(좁은 폭에서는 사이드바가 없으므로 화면 왼쪽 끝).
      left: compact ? 0 : LAYOUT.sidebarWidth,
      right: 0,
      zIndex: 1100,
      background: t.bgRaised,
      borderBottom: `1px solid ${t.border}`,
      boxShadow: SHADOW.raised,
      padding: `${SPACING.xs}px ${SPACING.xl}px`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: isRunning ? SPACING.xxs : 0,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACING.xs,
          fontSize: FONT_SIZE.sm, color: t.textPrimary,
        }}>
          {statusIcon}
          <span style={{ fontWeight: isRunning ? FONT_WEIGHT.regular : FONT_WEIGHT.semibold }}>{statusText}</span>
          {isRunning && (
            <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
              — 계속 작업하셔도 됩니다
            </span>
          )}
          {isDone && job!.failCount > 0 && (
            <span style={{ fontSize: FONT_SIZE.xs, color: warning }}>
              일부 실패가 있습니다
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
          {(isDone || isFailed) && (
            <AppButton
              variant="text" size="sm"
              icon={<ExpandOutlined />}
              onClick={onOpenDetail}
              style={{ color: t.textSecondary, ...(compact ? { height: 44 } : {}) /* 좁은 폭 누르는 곳 44px(감사 #518) */ }}
            >
              자세히 보기
            </AppButton>
          )}
          {(isDone || isFailed) && (
            <AppButton
              variant="text" size="sm"
              onClick={() => setVisible(false)}
              style={{ color: t.textSecondary, ...(compact ? { height: 44 } : {}) /* 좁은 폭 누르는 곳 44px(감사 #518) */ }}
            >
              닫기
            </AppButton>
          )}
        </div>
      </div>

      {isRunning && (
        <Progress
          percent={percent}
          showInfo={false}
          status="active"
          strokeColor={statusColor}
          trailColor={t.border}
          style={{ margin: 0 }}
          size={['100%', 3]}
        />
      )}
    </div>
  );
};